import {
  createAsyncThunk,
  createEntityAdapter,
  createSelector,
  createSlice,
} from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import type { Vendor } from "src/types";

import type { RootState } from "@/redux/root-reducer";
import endpoints, { handleAssetDomain } from "@/utils/endpoints";
import { authenticatedFetch } from "@/utils/fetchJSON";
import { isError } from "@/utils/is-error";

import { getState } from "../helpers";
import { getIsFirstLoading, getIsLoading } from "../requests/requests-reducer";

const get = endpoints();

export const fetchVendors = createAsyncThunk(
  "vendors/fetch",
  async (slug: string, { rejectWithValue }) => {
    const response = await authenticatedFetch.fetchJSON<Vendor[]>(
      get("VENDORS_API")(slug)
    );

    if (isError(response)) {
      toast.error(response.description, { toastId: response.description });
      rejectWithValue(response);
    }

    return response;
  }
);

export const vendorsAdapter = createEntityAdapter<Vendor>();

const vendorsSlice = createSlice({
  name: "vendors",
  initialState: vendorsAdapter.getInitialState(),
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(fetchVendors.fulfilled, (state, action) => {
      if (Array.isArray(action.payload)) {
        const entities = (action.payload as Vendor[])
          .filter((vendor: Vendor) => vendor.booth_size !== "mini")
          .map((vendor: Vendor) => ({
            ...vendor,
            background_image: handleAssetDomain(vendor.background_image),
            image_url: handleAssetDomain(vendor.image_url),
          }));
        vendorsAdapter.upsertMany(state, entities);
      }
    });
  },
});

const { selectAll: getAllVendors } = vendorsAdapter.getSelectors(
  (state: RootState) => state.vendors
);

const getIsVendorsLoading = createSelector(
  getState,
  getAllVendors,
  (state, vendors) => {
    return getIsLoading(state, fetchVendors) && !vendors.length;
  }
);

const getIsVendorsFirstLoading = createSelector(getState, (state) => {
  return getIsFirstLoading(state, fetchVendors);
});

export { getAllVendors, getIsVendorsLoading, getIsVendorsFirstLoading };

export default vendorsSlice.reducer;
